import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import {
  COUNTRIES_LIST,
  STATE_LIST,
  TIME_ZONE_LIST,
} from "../../../../constants";
import {
  PROVIDER_USER_INITIAL_VALUES,
  PROVIDER_USER_VALIDATION_SCHEMA,
  ProviderUserType,
} from "../../../../constants/ProviderUserSchema";
import { GENDER, PROVIDER_TYPE_LIST } from "../../../../constants/master";
import { useDebounce } from "../../../../hooks/use-debounce";
import { useFormScrollIntoView } from "../../../../hooks/use-form-scrollInto-view";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useProviderGroupProfile } from "../../../../queries/admin/provider-group/use-provider-group-profile";
import { useTenantConfiguration } from "../../../../queries/use-get-tenant-configuration";
import { useInsurancePayerList } from "../../../../queries/use-insurance-payer";
import { useLanguageList } from "../../../../queries/use-language-list";
import { useLocationsList } from "../../../../queries/use-locationList";
import { useSpecialtiesList } from "../../../../queries/use-specialities-list";
import { useUpdateProviderUserAvatar } from "../../../../queries/use-update-provider-avatar";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  convertToBase64,
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
} from "../../../../utils/utils";
import CustomDatePicker from "../../../CustomDatePicker";
import CustomSelect from "../../../CustomSelect";
import Flex from "../../../Flex";
import FormSwitch from "../../../FormSwitch";
import Input from "../../../Input";
import MaskInput from "../../../MaskInput";
import MaskMultiSelect from "../../../MaskMultiSelect";
import MaskMultiSelectScroll from "../../../MaskMultiSelectScroll";
import SideSheetDrawer from "../../../SidesheetDrawer";
import Text from "../../../Text";
import DropZone from "../../DropZone";
import DeaInformation from "./DeaInformation";
import LicensedInformation from "./LincensedInformation";
import useRoleOptions from "../../settings/user-settings/use-role-options";
import { CHART_TYPOGRAPHY } from "../../provider/patient/chart";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";
interface ProviderUserModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onSubmit: (values: ProviderUserType) => Promise<unknown>;
  /** An edit prefill built from the provider record, which may be absent. */
  prefillValues?: Partial<ProviderUserType>;
  prefillSpecialtyList?: any;
  shouldGetPrefillList?: boolean;
  isAdminAccessField?: boolean;
}

const UpsertProviderUserDrawer = ({
  open,
  title,
  onClose,
  onSubmit,
  prefillValues,
  shouldGetPrefillList,
  prefillSpecialtyList,
  isAdminAccessField,
}: ProviderUserModalProps) => {
  /* Callers distinguish the two modes through the title alone — there is no
     mode prop — so editing is whatever the submit handler already treats as
     editing. */
  const isEditMode = !!title?.includes("Edit");
  const param = useParams();
  const { data: TenantConfigurationData } = useTenantConfiguration({
    enabled: open,
  });
  const { data: providerGroupData }: any = useProviderGroupProfile(param.uuid);
  const [insuranceSearchKey, setInsuranceSearchKey] = useDebounce<string>("");
  const queryClient = useQueryClient();
  const realm = getStoredRealm();
  const viewAs = getViewAs();
  const { mutateAsync: changeProviderUserAvatar } =
    useUpdateProviderUserAvatar();
  const showSnackbar = useSnackbar();
  const [insurancePagination, setInsurancePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [specialtiesPagination, setSpecialtiesPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: insurancePayerList, isFetching: isInsuranceFetching } =
    useInsurancePayerList({
      xTENANTID: param.providerGroupId ?? undefined,
      searchKey: insuranceSearchKey,
      pagination: insurancePagination,
      enabled: open,
    });
  const { data: specialtiesList, isFetching: isSpecialtiesFetching } =
    useSpecialtiesList({
      xTENANTID: param.providerGroupId ?? realm ?? undefined,
      pagination: specialtiesPagination,
      enabled: open,
    });
  const { data } = useLanguageList({
    xTENANTID: viewAs === "provider" ? realm : param.providerGroupId,
    enabled: open,
  });
  const [searchKey, setSearchKey] = useDebounce<string>("");

  const { rolesData: rolesList } = useRoleOptions({
    enabled: open,
    pgiD: param?.providerGroupId ?? "",
    roleView: "provider",
  });

  const [locationPagination, setLocationPagination] = useState<DGT_Pagination>(
    DEF_PAGINATION
  );
  const { data: LocationList, isFetching: isLocationFetching } =
    useLocationsList({
      pagination: locationPagination,
      searchKey: searchKey,
      status: true,
      providerGroupId: viewAs === "provider" ? realm : param.providerGroupId,
      enabled: open,
    });

  const formik = useFormik({
    initialValues: prefillValues
      ? {
          ...prefillValues,
          address: {
            ...PROVIDER_USER_INITIAL_VALUES.address,
            ...(prefillValues?.address ?? {}),
          },
        }
      : {
          ...PROVIDER_USER_INITIAL_VALUES,
          timezone:
            providerGroupData?.timezone ??
            TenantConfigurationData?.data?.timeZone,
        },
    validationSchema: PROVIDER_USER_VALIDATION_SCHEMA,
    onSubmit: (v) => {
      const getRoleName = rolesList.find(
        (role) => role.value === v.role
      )?.label;

      onSubmit(
        // An edit prefill is a Partial, so the form values are too; Yup has
        // already enforced the fields `ProviderUserType` declares required.
        getUnNecessaryTransformData(
          ["ageGroupSeen", "careService", "spokenLanguages"],
          {
            ...(v ?? {}),
            avatar: isEditMode ? "" : v.avatar,
            firstName: v?.firstName?.trim(),
            lastName: v?.lastName?.trim(),
            role: getRoleName,
          }
        ) as ProviderUserType
      )
        ?.then((res) => {
          if (
            isEditMode &&
            // v.avatar &&
            !v?.avatar?.includes(".amazonaws.com")
          ) {
            changeProviderUserAvatar({
              newavatar: v.avatar,
              providerUuid: v?.uuid ?? undefined,
              xTENANTID: param?.providerGroupId,
            }).then(() => {
              queryClient.invalidateQueries({
                queryKey: ["providers-List"],
              });
              queryClient.invalidateQueries({
                queryKey: ["provider-profile"],
              });
            });
          }
          showSnackbar(getSuccessSnackbar(res));
          queryClient.invalidateQueries({
            queryKey: ["providers-List"],
          });

          closeAndReset();
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    },
    enableReinitialize: true,
  });

  useFormScrollIntoView(formik);

  function closeAndReset() {
    onClose();

    formik.resetForm();
  }

  const specialitiesOptions = useMemo(() => {
    if (shouldGetPrefillList) return (prefillSpecialtyList ?? []) as any[];
    const fetched = (specialtiesList?.data?.content ?? []) as {
      [k: string]: string;
    }[];
    const selected = (formik.values.specialities ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [
    shouldGetPrefillList,
    prefillSpecialtyList,
    specialtiesList,
    formik.values.specialities,
  ]);

  const insuranceOptions = useMemo(() => {
    const fetched =
      insurancePayerList?.data.content?.map((insurance) => ({
        value: insurance.payerId,
        label: insurance.payerName,
      })) ?? [];
    const selected = (formik.values?.acceptedInsurances ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) =>
        !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [insurancePayerList, formik.values?.acceptedInsurances]);

  const workLocationsOptions = useMemo(() => {
    const fetched =
      LocationList?.data?.content?.map((it) => ({
        value: it?.uuid ?? "",
        label: it?.name ?? "",
      })) ?? [];
    const selected = (formik.values?.workLocations ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) =>
        !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [LocationList, formik.values?.workLocations]);

  return (
    <SideSheetDrawer
      title={title}
      open={open}
      w="60vw"
      onClose={closeAndReset}
      footerActions={
        <Button
          onClick={formik.submitForm}
          variant="contained"
          style={{ width: "100px" }}
        >
          Save
        </Button>
      }
    >
      <Flex flexDirection={"column"} width="100%">
        <Flex gap={"10px"}>
          {isAdminAccessField && (
            <Box mb={"10px"}>
              <Text>Admin Access</Text>
              <FormSwitch
                checked={formik.values?.admin_access ?? false}
                onChange={(e) =>
                  formik.setFieldValue("admin_access", e.target.checked)
                }
              />
            </Box>
          )}
        </Flex>

        <Flex gap={"0.825rem"}>
          <Grid container spacing={2}>
            <Grid item xs={6} lg={2} xl={1.5}>
              <Input
                required
                label="First Name"
                textFieldProps={{
                  placeholder: "First Name",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "firstName",
                  value: formik.values.firstName,
                }}
                error={formik.touched?.firstName && formik.errors?.firstName}
              />
            </Grid>
            <Grid item xs={6} lg={2} xl={1.5}>
              <Input
                required
                label="Last Name"
                textFieldProps={{
                  placeholder: "Last Name",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "lastName",
                  value: formik.values.lastName,
                }}
                error={formik.touched?.lastName && formik.errors?.lastName}
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <CustomSelect
                label="Provider Type"
                options={PROVIDER_TYPE_LIST}
                value={formik.values.providerType}
                onChange={(value) => {
                  formik.setFieldValue("providerType", value ?? undefined);
                }}
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <MaskMultiSelectScroll
                label="Specialities"
                options={specialitiesOptions}
                name="specialities"
                handleBlur={formik.handleBlur}
                optionWithCheckbox={true}
                value={formik.values.specialities}
                onChange={(values) => {
                  formik.setFieldValue("specialities", values);
                }}
                error={
                  formik.touched.specialities && formik.errors.specialities
                }
                pagination={specialtiesPagination}
                setPagination={setSpecialtiesPagination}
                totalElements={specialtiesList?.data?.page?.totalElements}
                loading={isSpecialtiesFetching}
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <CustomSelect
                required
                label="Role"
                name="role"
                handleBlur={formik.handleBlur}
                options={rolesList}
                value={formik.values.role}
                onChange={(option) => formik.setFieldValue("role", option)}
                error={formik.touched.role && formik.errors.role}
              />
            </Grid>
            <Grid item container xs={6} lg={4} xl={3} spacing={1}>
              <Grid item xs={6} lg={6} xl={6}>
                <CustomDatePicker
                  maxDate={dayjs(new Date())}
                  value={formik.values.birthDate}
                  label="DOB"
                  onChange={(val) => {
                    formik.setFieldValue("birthDate", val);
                  }}
                />
              </Grid>
              <Grid item xs={6} lg={6} xl={6}>
                <CustomSelect
                  required
                  label="Gender"
                  name="gender"
                  handleBlur={formik.handleBlur}
                  options={GENDER}
                  value={formik.values.gender}
                  onChange={(option) => formik.setFieldValue("gender", option)}
                  error={formik.touched.gender && formik.errors.gender}
                />
              </Grid>
            </Grid>

            <Grid item xs={6} lg={4} xl={3}>
              <Input
                required={!formik.values.doesNotHaveNpi}
                label="NPI Number"
                textFieldProps={{
                  placeholder: "Enter Number",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "npi",
                  value: formik.values.npi,
                  disabled: formik.values.doesNotHaveNpi,
                }}
                error={formik.touched?.npi && formik.errors?.npi}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formik.values.doesNotHaveNpi ?? false}
                    onChange={(e) => {
                      formik.setFieldValue("doesNotHaveNpi", e.target.checked);
                      if (e.target.checked) {
                        formik.setFieldValue("npi", "");
                        formik.setFieldTouched("npi", false);
                      }
                    }}
                  />
                }
                label={
                  <Text fontSize={CHART_TYPOGRAPHY.body5.fontSize}>
                    Doesn't have NPI Number
                  </Text>
                }
              />
            </Grid>
            <Grid item xs={6} lg={2} xl={3}>
              <MaskInput
                mask={"(999) 999-9999"}
                label="Contact Number"
                name="phone"
                error={formik.errors?.phone}
                placeholder="Enter Contact Number"
                value={formik.values.phone}
                onChange={(e) => formik.setFieldValue("phone", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} lg={2} xl={3}>
              <MaskInput
                required
                mask={"99-9999999"}
                label="Federal Tax ID Number"
                name="federalTaxIdNumber"
                placeholder="Enter Number"
                value={formik.values?.federalTaxIdNumber}
                onChange={(e) =>
                  formik.setFieldValue(
                    "federalTaxIdNumber",
                    e.target.value.replace(/\D/g, "")
                  )
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched?.federalTaxIdNumber &&
                  formik.errors?.federalTaxIdNumber
                }
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <Input
                label="Office Fax Number"
                textFieldProps={{
                  placeholder: "Enter Number",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "officeFaxNumber",
                  value: formik.values?.officeFaxNumber,
                }}
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <Input
                label="Group NPI Number"
                textFieldProps={{
                  disabled: true,
                  placeholder: "Enter Number",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "groupNpiNumber",
                  value: formik.values.groupNpiNumber,
                }}
              />
            </Grid>
            {/* <Grid item xs={6} lg={4} xl={3}>
              <MaskMultiSelect
                label="Licensed State(s)"
                options={STATE_LIST}
                optionWithCheckbox
                value={formik.values?.licensedStates}
                onChange={(values) => {
                  formik.setFieldValue("licensedStates", values);
                }}
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <Input
                label="License Number"
                textFieldProps={{
                  placeholder: "Enter Number",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "licenseNumber",
                  value: formik.values.licenseNumber,
                }}
              />
            </Grid> */}
            <Grid item xs={6} lg={4} xl={3}>
              <MaskMultiSelectScroll
                label="Insurance(s) Accepted"
                options={insuranceOptions}
                onSearch={setInsuranceSearchKey}
                optionWithCheckbox={true}
                value={formik.values?.acceptedInsurances}
                onChange={(values) => {
                  formik.setFieldValue("acceptedInsurances", values);
                }}
                pagination={insurancePagination}
                setPagination={setInsurancePagination}
                totalElements={insurancePayerList?.data?.page?.totalElements}
                loading={isInsuranceFetching}
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <Input
                label="Taxonomy Number"
                textFieldProps={{
                  placeholder: "Enter Number",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "taxonomyNumber",
                  value: formik.values.taxonomyNumber,
                }}
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <MaskMultiSelectScroll
                label="Work Locations"
                options={workLocationsOptions}
                onSearch={setSearchKey}
                optionWithCheckbox={true}
                value={formik.values?.workLocations}
                onChange={(values) => {
                  formik.setFieldValue("workLocations", values);
                }}
                pagination={locationPagination}
                setPagination={setLocationPagination}
                totalElements={LocationList?.data?.page?.totalElements}
                loading={isLocationFetching}
              />
            </Grid>
            <Grid item xs={6} lg={4} xl={3}>
              <Input
                required
                label="Email"
                textFieldProps={{
                  placeholder: "Enter Email",
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                  name: "email",
                  value: formik.values.email,
                }}
                error={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item sm={6} lg={4} xl={3}>
              <CustomSelect
                label="Time Zone"
                value={formik.values.timezone}
                options={TIME_ZONE_LIST}
                onChange={(val) => formik.setFieldValue("timezone", val)}
                typographyProps={{
                  onBlur: formik.handleBlur,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} lg={4}>
              <MaskMultiSelect
                label="Languages Spoken"
                options={data?.data?.content}
                placeholder="Select"
                optionWithCheckbox={true}
                value={formik.values?.spokenLanguages}
                onChange={(values) => {
                  formik.setFieldValue("spokenLanguages", values);
                }}
              />
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={12} sm={6} lg={4}>
                <Input
                  required
                  label="Address Line 1"
                  textFieldProps={{
                    placeholder: "Address Line 1",
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                    name: "address.line1",
                    value: formik.values.address?.line1,
                  }}
                  error={
                    (formik.touched.address as any)?.line1 &&
                    (formik.errors.address as any)?.line1
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Input
                  label="Address Line 2"
                  textFieldProps={{
                    placeholder: "Address Line 2",
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                    name: "address.line2",
                    value: formik.values.address?.line2,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Input
                  required
                  label="City"
                  textFieldProps={{
                    placeholder: "City",
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                    name: "address.city",
                    value: formik.values.address?.city,
                  }}
                  error={
                    (formik.touched.address as any)?.city &&
                    (formik.errors.address as any)?.city
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <CustomSelect
                  required
                  label="State"
                  value={formik.values.address?.state}
                  options={STATE_LIST}
                  onChange={(val) => formik.setFieldValue("address.state", val)}
                  placeholder="Select"
                  error={
                    (formik.touched.address as any)?.state &&
                    (formik.errors.address as any)?.state
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <CustomSelect
                  required
                  name="address.country"
                  value={formik.values.address?.country}
                  disableSortOptions
                  options={COUNTRIES_LIST}
                  onChange={(val) =>
                    formik.setFieldValue("address.country", val)
                  }
                  label="Country"
                  placeholder="Select Country"
                  error={
                    (formik.touched.address as any)?.country &&
                    (formik.errors.address as any)?.country
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Input
                  required
                  label="ZIP Code"
                  textFieldProps={{
                    name: "address.zipcode",
                    value: (() => {
                      const d = (formik.values.address?.zipcode || "")
                        .replace(/\D/g, "")
                        .slice(0, 9);
                      return d.length > 5
                        ? `${d.slice(0, 5)}-${d.slice(5)}`
                        : d;
                    })(),
                    onChange: (e) => {
                      const digits = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 9);
                      const val =
                        digits.length > 5
                          ? `${digits.slice(0, 5)}-${digits.slice(5)}`
                          : digits;
                      formik.setFieldValue("address.zipcode", val);
                    },
                    onBlur: formik.handleBlur,
                    inputProps: { maxLength: 10 },
                  }}
                  error={
                    (formik.touched.address as any)?.zipcode &&
                    (formik.errors.address as any)?.zipcode
                  }
                />
              </Grid>
            </Grid>
            <Grid item xs={12} lg={12} xl={12}>
              <Accordion
                sx={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  sx={{ paddingLeft: 0 }}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={"licenseAndDeaInformation"}
                  id={"licenseAndDeaInformation"}
                >
                  <Typography fontWeight={500}>
                    Licensed & DEA Information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Grid item xs={12} lg={12} xl={12}>
                    <LicensedInformation formik={formik as any} />
                  </Grid>

                  <Grid item xs={12} lg={12} xl={12}>
                    <DeaInformation formik={formik as any} />
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid container item sm={12} lg={12} xl={12} spacing={2}>
              <Grid item sm={12} lg={3} xl={3}>
                <DropZone
                  value={formik.values?.avatar}
                  label="Profile Photo"
                  content="Upload Profile Photo"
                  height={"190px"}
                  backgroundColor={"primary.light"}
                  uploadIcon={
                    <AddPhotoAlternateOutlinedIcon sx={{ fontSize: "4rem" }} />
                  }
                  onClick={async (files) => {
                    if (files) {
                      const base64File = await convertToBase64(files[0]);
                      formik.setFieldValue("avatar", base64File);
                    } else formik.setFieldValue("avatar", null);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Flex>

        <Box mt={2}>
          <Input
            label="Provider Bio"
            textFieldProps={{
              placeholder: "Enter Bio",
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              name: "bio",
              value: formik.values.bio,
              multiline: true,
              rows: 3,
            }}
          />
        </Box>
        {/* </OutlineCard> */}
      </Flex>
    </SideSheetDrawer>
  );
};

export default UpsertProviderUserDrawer;
